var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo, useState } from 'react';
import { Accordion, ConfirmDialog, InlineNotification, Table, Text, TextInput } from '@ui';
import { fetchRecentFailures, resendFailure } from '../shared/api';
import { KNOWN_FAILURE_STEPS } from '../shared/constants';
import { translatorFor } from '../shared/i18n';
import { LazyState, RefreshButton } from '../shared/LazySection';
import { useLazyResource } from '../shared/useLazyResource';
import { errorResponse, humanize } from '../shared/types';
import { ReasonCell, buildResendRenderer } from './FailureCells';
const t = translatorFor('recent_failures');
function stepLabel(step) {
    return KNOWN_FAILURE_STEPS.includes(step) ? t(`step_${step}`) : humanize(step);
}
function toRow(failure) {
    var _a;
    return {
        id: String(failure.id),
        recordId: failure.recordId,
        time: new Date(failure.createdAt).toLocaleString(),
        step: stepLabel(failure.step),
        reason: failure.reason,
        detail: (_a = failure.rawReason) !== null && _a !== void 0 ? _a : failure.responseBody
    };
}
export function RecentFailuresCard({ urls }) {
    var _a, _b, _c;
    const [expanded, setExpanded] = useState(false);
    const recent = useLazyResource(() => fetchRecentFailures(urls));
    const [resendTarget, setResendTarget] = useState(null);
    const [resendReason, setResendReason] = useState('');
    const [resending, setResending] = useState(false);
    const [resendMessage, setResendMessage] = useState(null);
    const canResend = (_b = (_a = recent.data) === null || _a === void 0 ? void 0 : _a.canResend) !== null && _b !== void 0 ? _b : false;
    const toggle = (next) => {
        setExpanded(next);
        if (next)
            recent.load();
    };
    const resend = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!resendTarget)
            return;
        setResending(true);
        try {
            yield resendFailure(urls, resendTarget.recordId, resendReason.trim());
            setResendMessage({ ok: true, text: t('resend_queued') });
            setResendTarget(null);
            setResendReason('');
            yield recent.refresh();
        }
        catch (err) {
            setResendMessage({ ok: false, text: (_b = (_a = errorResponse(err)) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : t('resend_failed') });
            setResendTarget(null);
        }
        finally {
            setResending(false);
        }
    });
    const rows = useMemo(() => { var _a, _b; return ((_b = (_a = recent.data) === null || _a === void 0 ? void 0 : _a.failures) !== null && _b !== void 0 ? _b : []).map(toRow); }, [recent.data]);
    const columns = useMemo(() => [
        { dataKey: 'time', label: t('time_heading'), columnType: 'primary', width: '20%' },
        { dataKey: 'recordId', label: t('job_heading'), width: '15%' },
        { dataKey: 'step', label: t('step_heading'), width: '15%' },
        { dataKey: 'reason', label: t('reason_heading'), width: canResend ? '35%' : '50%', renderer: ReasonCell },
        ...(canResend
            ? [
                {
                    dataKey: 'actions',
                    label: '',
                    columnType: 'action',
                    width: '15%',
                    renderer: buildResendRenderer(setResendTarget)
                }
            ]
            : [])
    ], [canResend]);
    return (React.createElement(Accordion, { variant: "section", title: t('title'), isExpanded: expanded, onToggle: toggle },
        React.createElement("div", { id: "service-titan-recent-failures", className: "service-titan-settings__accordion-body" },
            React.createElement(LazyState, { loading: recent.loading, error: recent.error, onRetry: () => {
                    recent.reload();
                } }),
            resendMessage && (React.createElement(InlineNotification, { variant: resendMessage.ok ? 'success' : 'error' }, resendMessage.text)),
            recent.data && (React.createElement(React.Fragment, null,
                React.createElement(Table, { data: rows, columns: columns, getItemId: (row) => row.id, emptyContent: React.createElement(Text, { variant: "body" }, t('empty')), variant: "listing" }),
                React.createElement(RefreshButton, { disabled: recent.loading, onRefresh: () => {
                        recent.reload();
                    } }))),
            React.createElement(ConfirmDialog, { isOpen: resendTarget !== null, title: t('resend_title'), description: React.createElement(React.Fragment, null,
                    React.createElement(Text, { variant: "body", as: "p" }, t('resend_description', { job: (_c = resendTarget === null || resendTarget === void 0 ? void 0 : resendTarget.recordId) !== null && _c !== void 0 ? _c : '' })),
                    React.createElement(TextInput, { id: "service-titan-resend-reason", label: t('resend_reason_label'), required: true, value: resendReason, onChange: (event) => setResendReason(event.target.value) })), confirmLabel: t('resend'), cancelLabel: t('cancel'), loading: resending, onConfirm: () => {
                    if (resendReason.trim() !== '')
                        void resend();
                }, onCancel: () => setResendTarget(null) }))));
}
export default RecentFailuresCard;
